/**
 * Category tabs with AJAX product grid loading.
 */
const TAB_SCROLL_AMOUNT = 240;
const SELECTORS = {
    root: '[data-category-tabs]',
    scroll: '[data-category-tabs-scroll]',
    track: '[data-category-tabs-track]',
    prev: '[data-category-tabs-prev]',
    next: '[data-category-tabs-next]',
    tab: '.category-tabs__tab',
    panel: '[data-category-tabs-panel]',
    grid: '[data-category-tabs-grid]',
    viewAll: '[data-category-tabs-view-all]',
};
export function initCategoryTabs() {
    if (!window.distinctLaptopsCategoryTabs) {
        return;
    }
    document.querySelectorAll(SELECTORS.root).forEach((root) => {
        CategoryTabs.create(root);
    });
}
class CategoryTabs {
    constructor(root) {
        this.cache = new Map();
        this.abortController = null;
        this.activeSlug = '';
        this.isLoading = false;
        this.root = root;
        this.config = window.distinctLaptopsCategoryTabs;
        this.tabTrack = root.querySelector(SELECTORS.track);
        this.prevBtn = root.querySelector(SELECTORS.prev);
        this.nextBtn = root.querySelector(SELECTORS.next);
        this.tabs = Array.from(root.querySelectorAll(SELECTORS.tab));
        this.panel = root.querySelector(SELECTORS.panel);
        this.grid = root.querySelector(SELECTORS.grid);
        this.viewAll = root.querySelector(SELECTORS.viewAll);
        this.activeSlug = this.grid.dataset.activeSlug ?? '';
        this.seedCache();
        this.bind();
        this.bindTabScroll();
        this.updateScrollButtons();
    }
    static create(root) {
        new CategoryTabs(root);
    }
    seedCache() {
        const activeTab = this.tabs.find((tab) => tab.classList.contains('category-tabs__tab--active'));
        if (!activeTab || !this.activeSlug) {
            return;
        }
        this.cache.set(this.activeSlug, {
            html: this.grid.innerHTML,
            categoryUrl: activeTab.dataset.categoryUrl ?? this.viewAll.href,
        });
    }
    bind() {
        this.tabs.forEach((tab) => {
            tab.addEventListener('click', () => this.onTabClick(tab));
            tab.addEventListener('keydown', (event) => this.onTabKeydown(event, tab));
        });
    }
    bindTabScroll() {
        this.prevBtn?.addEventListener('click', () => this.scrollTabs(-TAB_SCROLL_AMOUNT));
        this.nextBtn?.addEventListener('click', () => this.scrollTabs(TAB_SCROLL_AMOUNT));
        this.tabTrack?.addEventListener('scroll', () => this.updateScrollButtons(), { passive: true });
        window.addEventListener('resize', () => this.updateScrollButtons(), { passive: true });
    }
    scrollTabs(offset) {
        this.tabTrack?.scrollBy({ left: offset, behavior: 'smooth' });
    }
    updateScrollButtons() {
        if (!this.tabTrack) {
            return;
        }
        const { scrollLeft, scrollWidth, clientWidth } = this.tabTrack;
        const atStart = scrollLeft <= 4;
        const atEnd = scrollLeft + clientWidth >= scrollWidth - 4;
        if (this.prevBtn) {
            this.prevBtn.disabled = atStart;
        }
        if (this.nextBtn) {
            this.nextBtn.disabled = atEnd;
        }
    }
    onTabClick(tab) {
        const slug = tab.dataset.categorySlug ?? '';
        if (!slug || slug === this.activeSlug || this.isLoading) {
            return;
        }
        this.setActiveTab(tab);
        void this.loadCategory(slug);
    }
    onTabKeydown(event, tab) {
        const index = this.tabs.indexOf(tab);
        if (index < 0) {
            return;
        }
        let targetIndex = -1;
        if (event.key === 'ArrowRight') {
            targetIndex = (index + 1) % this.tabs.length;
        }
        else if (event.key === 'ArrowLeft') {
            targetIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        }
        else if (event.key === 'Home') {
            targetIndex = 0;
        }
        else if (event.key === 'End') {
            targetIndex = this.tabs.length - 1;
        }
        else {
            return;
        }
        event.preventDefault();
        this.tabs[targetIndex].focus();
        this.onTabClick(this.tabs[targetIndex]);
    }
    setActiveTab(tab) {
        const slug = tab.dataset.categorySlug ?? '';
        const tabId = tab.id;
        this.tabs.forEach((item) => {
            const isActive = item === tab;
            item.classList.toggle('category-tabs__tab--active', isActive);
            item.setAttribute('aria-selected', isActive ? 'true' : 'false');
            item.tabIndex = isActive ? 0 : -1;
        });
        this.activeSlug = slug;
        this.grid.dataset.activeSlug = slug;
        this.panel.setAttribute('aria-labelledby', tabId);
        tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        this.updateScrollButtons();
    }
    async loadCategory(slug) {
        const cached = this.cache.get(slug);
        if (cached) {
            this.applyGrid(cached.html, cached.categoryUrl);
            return;
        }
        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;
        this.setLoading(true);
        this.showSkeletons();
        try {
            const data = await this.fetchCategory(slug, signal);
            if (!data) {
                return;
            }
            const entry = {
                html: data.html,
                categoryUrl: data.category_url,
            };
            this.cache.set(slug, entry);
            this.applyGrid(entry.html, entry.categoryUrl);
        }
        catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                return;
            }
        }
        finally {
            this.setLoading(false);
        }
    }
    async fetchCategory(slug, signal) {
        const body = new FormData();
        body.append('action', this.config.action);
        body.append('nonce', this.config.nonce);
        body.append('slug', slug);
        const response = await fetch(this.config.ajaxUrl, {
            method: 'POST',
            body,
            credentials: 'same-origin',
            signal,
        });
        if (!response.ok) {
            return null;
        }
        const json = (await response.json());
        if (!json.success || !json.data) {
            return null;
        }
        return json.data;
    }
    showSkeletons() {
        const skeletons = Array.from({ length: this.config.limit }, () => {
            const el = document.createElement('div');
            el.className = 'category-tabs__skeleton';
            el.setAttribute('aria-hidden', 'true');
            return el.outerHTML;
        });
        this.grid.innerHTML = skeletons.join('');
    }
    applyGrid(html, categoryUrl) {
        this.panel.classList.add('is-fading');
        window.setTimeout(() => {
            this.grid.innerHTML = html;
            this.viewAll.href = categoryUrl;
            this.panel.classList.remove('is-fading');
        }, 150);
    }
    setLoading(loading) {
        this.isLoading = loading;
        this.panel.classList.toggle('is-loading', loading);
        this.root.classList.toggle('is-loading', loading);
    }
}
